/**
 * Indicates if the browser supports passive event listeners
 *
 * @see https://web.dev/uses-passive-event-listeners/?utm_source=lighthouse&utm_medium=unknown
 * @see https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
 * @returns {boolean}
 */
export const supportsPassiveEvents = () => {
    let supported = false;

    try {
        const noop = () => ({});
        const options = {
            get passive() {
                return (supported = true);
            },
        };

        window.addEventListener('test', noop, options);
        window.removeEventListener('test', noop, options);
    } catch (error) {
        supported = false;
    }

    return supported;
};

/**
 * Checks whether or not it's an HTML Element
 *
 * @param {HTMLElement} el HTML Element
 * @returns {Boolean}
 */
export const isHTMLElement = (el) => el && el instanceof HTMLElement;

/**
 * Add events
 *
 * @param HTMLElement el Any dom element
 * @param string events Name of the event, you can also use multiple separated by `,`.
 * @param Function callback
 * @param {boolean|Object} options
 * @example
 * on(el, 'dragstart, touchstart', clb);
 */
export const on = (el, events, callback, options = false) => {
    events.split(',').forEach((ev) => el.addEventListener(ev.trim(), callback, options));
};

/**
 * Remove events
 *
 * @param HTMLElement el Any dom element
 * @param string events Name of the event, you can also use multiple separated by `,`.
 * @param Function callback
 * @example
 * off(el, 'dragstart, touchstart', clb);
 */
export const off = (el, events, callback, options = false) => {
    events.split(',').forEach((ev) => el.removeEventListener(ev.trim(), callback, options));
};

/**
 * Get cross-device pointer object
 *
 * @param Event ev
 * @returns Event
 */
export const getPointerObj = (ev = {}) => (ev.touches ? ev.touches[0] : ev);

/**
 * Checks whether a class exists on a specific element
 *
 * @param {HTMLElement} el HTML Element
 * @param {String} className Class name
 *
 * @returns {Boolean}
 */
export const hasClass = (el, className) => {
    if (!isHTMLElement(el)) {
        return false;
    }

    if (el.classList) {
        return el.classList.contains(className);
    } else {
        return !!el.className.match(new RegExp(`(\\s|^)${className}(\\s|$)`));
    }
};

/**
 * Remove a class on a specific element
 *
 * @param {HTMLElement} el HTML Element
 * @param {String} className Class name
 *
 * @returns {Void|Boolean}
 */
export const removeClass = (el, className) => {
    if (!isHTMLElement(el)) {
        return false;
    }

    const classList = className.split(' ');

    if (el.classList) {
        el.classList.remove(classList[0]);
    } else if (hasClass(el, classList[0])) {
        const reg = new RegExp(`(\\s|^)${classList[0]}(\\s|$)`);
        el.className = el.className.replace(reg, ' ');
    }

    if (classList.length > 1) {
        removeClass(el, classList.slice(1).join(' '));
    }
};

/**
 *
 * @param {HTMLElement} el HTML Element
 * @param {String} className Class name
 *
 * @returns {Void|Boolean}
 */
export const addClass = (el, className) => {
    if (!isHTMLElement(el)) {
        return false;
    }

    const classList = className.split(' ');

    if (el.classList) {
        el.classList.add(classList[0]);
    } else if (hasClass(el, classList[0])) {
        el.className += ` ${classList[0]}`;
    }

    if (classList.length > 1) {
        addClass(el, classList.slice(1).join(' '));
    }
};

/**
 * Toggle class based on boolean value
 *
 * @param {HTMLElement} el HTML Element
 * @param {String} className Class name
 * @param {Boolean} bool Whether to add the class or not
 */
export const toggleClass = (el, className, bool) => {
    if (bool) {
        addClass(el, className);
    } else {
        removeClass(el, className);
    }
};

/**
 * Get the current top/left coordinates of an element relative to the document.
 *
 * @param {HTMLElement} el HTML Element
 * @returns {Object}
 */
export const offset = (el) => {
    const rect = el.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    return {
        top: rect.top + scrollTop,
        left: rect.left + scrollLeft,
    };
};

/**
 * Get the horizontal and vertical scroll position of window element or the document.
 *
 * @param {String} [axis="y"] axis
 * @returns {number} position
 */
export const getWindowScrollPosition = (axis = 'y') =>
    window[`page${axis.toUpperCase()}Offset`] ||
    document.documentElement[axis.toUpperCase() === 'X' ? 'scrollLeft' : 'scrollTop'];

/**
 * Get the visibility of the element
 *
 * @param {HTMLElement} el HTML Element
 * @returns {Boolean}
 */
export const isVisible = (el) => isHTMLElement(el) && el.offsetParent !== null;
